import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { FilterAutocomplete, Table } from "../../../../components/ui";
import usePagination from "../../../../hooks/usePagination";
import { PASSES_COLUMNS } from "../../constants";
import { complianceSearchActions } from "../../../../reducers/complianceSearch";
import { useComplianceSearchContext } from "../../../../context";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../../api/compliance";
import { appendCommaSeparatedValuesToQuery } from "../../utils";
import { SectionTitle } from "../../styledComponents";

const HeliosPassesSearch: React.FC = () => {
  const pagination = usePagination(100);

  const dispatch = useDispatch();
  const {
    data: { results },
    loading,
  } = useSelector((state) => state.complianceSearch.watchlistSubmissionToProp);

  const { tabsTablesSearchState, setTabsTablesSearchState } = useComplianceSearchContext();
  const { names, compliance_entries_id } =
    tabsTablesSearchState.fundedAccountQuickSearchTab.heliosPassesSearchTable.search;

  const { isError, error } = useQuery({
    enabled: names?.length >= 2 || compliance_entries_id?.length > 0,
    queryKey: [
      COMPLIANCE_API_URLS.getPasses,
      names,
      compliance_entries_id,
      pagination.page,
      pagination.limit,
    ],
    async queryFn({ signal }) {
      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());

      names && appendCommaSeparatedValuesToQuery(names, query, "names");
      compliance_entries_id &&
        appendCommaSeparatedValuesToQuery(compliance_entries_id, query, "compliance_entries_id");

      dispatch(complianceSearchActions.fetchWatchlistPassesRequest());
      const response = await ComplianceAPI.getWatchlistPassesRequest(query.toString(), signal);
      pagination.setCount(response.data.results.count);
      dispatch(complianceSearchActions.fetchWatchlistPassesSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching passes", error);
      dispatch(complianceSearchActions.fetchWatchlistPassesFailed());
    }
  }, [isError, error]);

  useEffect(() => {
    pagination.setPage(0);
  }, [names, pagination.limit]);

  return (
    <SectionTitle title="Passes">
      <FilterAutocomplete
        search={{
          compliance_entries_id,
          names,
        }}
        setSearch={(values) => {
          setTabsTablesSearchState({
            ...tabsTablesSearchState,
            fundedAccountQuickSearchTab: {
              ...tabsTablesSearchState.fundedAccountQuickSearchTab,
              heliosPassesSearchTable: {
                search: {
                  names: values.names,
                  compliance_entries_id: values.compliance_entries_id,
                },
              },
            },
          });
        }}
        filters={{
          EntriesId: "compliance_entries_id",
          Name: "names",
        }}
      />

      <Table
        columns={PASSES_COLUMNS}
        data={results.rows}
        loading={loading}
        pagination={pagination}
        extractKey={(row) => row.id}
        csv_table_name="Funded account quick search - Passes"
      />
    </SectionTitle>
  );
};

export default HeliosPassesSearch;
